/**
 * Sequencing System — frontend API client.
 *
 * Phase 1A surface: groups CRUD, type catalog CRUD, disclaimers,
 * holidays, and Control-Plane passthroughs (devices + parameters).
 *
 * All calls use the shared `api` instance so they pick up the
 * `X-User-Email` header set by the AuthProvider.
 */
import api from "@/lib/api";

// ── system status ─────────────────────────────────────────────────
export const seqStatus = () =>
  api.get("/sequencing/system/status").then(r => r.data);

// ── groups ─────────────────────────────────────────────────────────
export const seqListGroups = (tenant_id) =>
  api.get("/sequencing/groups", { params: tenant_id ? { tenant_id } : {} }).then(r => r.data);
export const seqGetGroup = (gid) =>
  api.get(`/sequencing/groups/${gid}`).then(r => r.data);
export const seqCreateGroup = (payload) =>
  api.post("/sequencing/groups", payload).then(r => r.data);
export const seqPatchGroup = (gid, payload) =>
  api.patch(`/sequencing/groups/${gid}`, payload).then(r => r.data);
export const seqDeleteGroup = (gid) =>
  api.delete(`/sequencing/groups/${gid}`).then(r => r.data);
export const seqAddMember = (gid, payload) =>
  api.post(`/sequencing/groups/${gid}/members`, payload).then(r => r.data);
export const seqRemoveMember = (gid, deviceId) =>
  api.delete(`/sequencing/groups/${gid}/members/${deviceId}`).then(r => r.data);
// ── iter150AG Part 18 — manual start / stop toggle ──────────────────
export const seqMemberStartStop = (gid, deviceId, action, reason) =>
  api.post(`/sequencing/groups/${gid}/members/${deviceId}/start-stop`,
            { action, reason }).then(r => r.data);

// ── iter150AG Part 20 — telemetry diagnostics + self-heal ───────────
export const seqTelemetryDiagnostics = (gid) =>
  api.get(`/sequencing/groups/${gid}/telemetry-diagnostics`).then(r => r.data);
export const seqHealDeviceIds = (gid) =>
  api.post(`/sequencing/groups/${gid}/heal-device-ids`,
            { confirm: true }).then(r => r.data);

// ── iter150AG Part 21 — webhook capture mode ────────────────────────
export const seqCpWebhookCaptureStart = (maxCaptures = 20, ttlSeconds = 120) =>
  api.post(`/sequencing/admin/cp-webhook/capture/start`,
            { max_captures: maxCaptures, ttl_seconds: ttlSeconds }).then(r => r.data);
export const seqCpWebhookCaptureStop = () =>
  api.post(`/sequencing/admin/cp-webhook/capture/stop`).then(r => r.data);
export const seqCpWebhookCaptureList = (limit = 50) =>
  api.get(`/sequencing/admin/cp-webhook/capture/list`, { params: { limit } }).then(r => r.data);

// ── iter150AG Part 24 — dry-run probe ───────────────────────────────
export const seqTelemetryProbe = (gid) =>
  api.post(`/sequencing/groups/${gid}/telemetry-probe`).then(r => r.data);

// ── iter150AG Part 26 — AI rotation-policy recommender ──────────────
export const seqPolicyRecommendation = (gid, forceRefresh = false) =>
  api.post(`/sequencing/groups/${gid}/policy-recommendation`,
            { force_refresh: !!forceRefresh }).then(r => r.data);
export const seqPolicyRecommendationLatest = (gid) =>
  api.get(`/sequencing/groups/${gid}/policy-recommendation/latest`).then(r => r.data);

// ── Phase 1B — per-device control mode + FAD@Pset ───────────────────
export const seqGetControlMode = (deviceId) =>
  api.get(`/sequencing/devices/${deviceId}/control-mode`).then(r => r.data);
export const seqSetControlMode = (deviceId, mode) =>
  api.put(`/sequencing/devices/${deviceId}/control-mode`, { mode }).then(r => r.data);
export const seqListFad = (deviceId) =>
  api.get(`/sequencing/devices/${deviceId}/fad-at-pset`).then(r => r.data);
export const seqUpsertFad = (deviceId, pset_bar, fad_m3_per_min) =>
  api.put(`/sequencing/devices/${deviceId}/fad-at-pset`,
          { pset_bar, fad_m3_per_min }).then(r => r.data);
export const seqDeleteFad = (deviceId, pset_bar) =>
  api.delete(`/sequencing/devices/${deviceId}/fad-at-pset/${pset_bar}`).then(r => r.data);

// ── Phase 1B — schedule slots ───────────────────────────────────────
export const seqListSchedule = (gid) =>
  api.get(`/sequencing/groups/${gid}/schedule`).then(r => r.data);
export const seqUpsertScheduleSlot = (gid, payload) =>
  api.put(`/sequencing/groups/${gid}/schedule`, payload).then(r => r.data);
export const seqDeleteScheduleSlot = (gid, weekday) =>
  api.delete(`/sequencing/groups/${gid}/schedule/${weekday}`).then(r => r.data);
// iter150AH — atomic multi-window replace. Body: { slots: [{weekday,start_time,end_time}] }
export const seqReplaceSchedule = (gid, slots) =>
  api.put(`/sequencing/groups/${gid}/schedule/bulk`, { slots }).then(r => r.data);
// iter150AH — capacity analysis (V_system, Q_leak, leak%) from latest fill+loss pair.
export const seqCapacityAnalysis = (gid) =>
  api.get(`/sequencing/groups/${gid}/capacity-analysis`).then(r => r.data);
// iter150AH — quote-ready PDF of the capacity analysis.
export const seqCapacityAnalysisPdfUrl = (gid) => {
  const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${base}/api/sequencing/groups/${gid}/capacity-analysis.pdf`;
};
// iter150AH — sustained-leak alert state + manual trigger.
export const seqLeakAlertStatus = (gid) =>
  api.get(`/sequencing/groups/${gid}/leak-alert-status`).then(r => r.data);
export const seqLeakAlertSend = (gid, force = false) =>
  api.post(`/sequencing/groups/${gid}/leak-alert-send`, { force }).then(r => r.data);

// ── Phase 1B — fill / loss tests ────────────────────────────────────
export const seqListFillTests = (gid, limit = 50) =>
  api.get(`/sequencing/groups/${gid}/fill-tests`, { params: { limit } }).then(r => r.data);
export const seqStartFillTest = (gid, payload) =>
  api.post(`/sequencing/groups/${gid}/fill-tests`, payload).then(r => r.data);
export const seqCompleteFillTest = (tid, payload) =>
  api.patch(`/sequencing/fill-tests/${tid}/complete`, payload).then(r => r.data);
export const seqListLossTests = (gid, limit = 50) =>
  api.get(`/sequencing/groups/${gid}/loss-tests`, { params: { limit } }).then(r => r.data);
export const seqStartLossTest = (gid, payload) =>
  api.post(`/sequencing/groups/${gid}/loss-tests`, payload).then(r => r.data);
export const seqCompleteLossTest = (tid, payload) =>
  api.patch(`/sequencing/loss-tests/${tid}/complete`, payload).then(r => r.data);

// ── Phase 1B — read-only sequencing decisions log ───────────────────
export const seqListDecisions = (gid, limit = 100) =>
  api.get(`/sequencing/groups/${gid}/decisions`, { params: { limit } }).then(r => r.data);

// ── Phase 1C — engine controls per group + worker status ────────────
export const seqGroupEngine = (gid) =>
  api.get(`/sequencing/groups/${gid}/engine`).then(r => r.data);
export const seqGroupEngineEnable = (gid) =>
  api.post(`/sequencing/groups/${gid}/engine/enable`).then(r => r.data);
export const seqGroupEngineDisable = (gid) =>
  api.post(`/sequencing/groups/${gid}/engine/disable`).then(r => r.data);
export const seqGroupEngineTickNow = (gid) =>
  api.post(`/sequencing/groups/${gid}/engine/tick-now`).then(r => r.data);
export const seqEngineWorkerStatus = () =>
  api.get(`/sequencing/engine/status`).then(r => r.data);

// ── Phase 1D — commissioning FSM ────────────────────────────────────
export const seqFsmStatus = (gid) =>
  api.get(`/sequencing/groups/${gid}/fsm`).then(r => r.data);
export const seqFsmAdvance = (gid, target, payload = {}) =>
  api.post(`/sequencing/groups/${gid}/fsm/advance`, { target, payload }).then(r => r.data);
export const seqFsmReset = (gid) =>
  api.post(`/sequencing/groups/${gid}/fsm/reset`).then(r => r.data);
export const seqFsmTransitions = (gid, limit = 50) =>
  api.get(`/sequencing/groups/${gid}/fsm/transitions`, { params: { limit } }).then(r => r.data);
export const seqAssignMemberType = (gid, deviceId, typeCode) =>
  api.put(`/sequencing/groups/${gid}/members/${encodeURIComponent(deviceId)}/type`,
          { type_code: typeCode }).then(r => r.data);
export const seqListMemberTypes = (gid) =>
  api.get(`/sequencing/groups/${gid}/members/types`).then(r => r.data);
export const seqAdminMockCommands = (limit = 50) =>
  api.get(`/sequencing/admin/mock-commands`, { params: { limit } }).then(r => r.data);

// ── Phase 1E — Live dashboard snapshot ──────────────────────────────
export const seqGroupDashboard = (gid) =>
  api.get(`/sequencing/groups/${gid}/dashboard`).then(r => r.data);

// ── iter150AG Part 9 (P2) — fault alerts (open/closed) per group ────
export const seqGroupAlerts = (gid, limit_closed = 20) =>
  api.get(`/sequencing/groups/${gid}/alerts`, { params: { limit_closed } }).then(r => r.data);

// ── Phase 1A — Super-admin Control-Plane config modal ───────────────
export const seqCpConfigGet = () =>
  api.get(`/sequencing/admin/control-plane/config`).then(r => r.data);
export const seqCpConfigSave = (base_url, api_key, opts = {}) =>
  api.post(`/sequencing/admin/control-plane/config`, {
    base_url, api_key,
    ...(opts.webhook_secret !== undefined ? { webhook_secret: opts.webhook_secret } : {}),
    ...(opts.ws_fallback_enabled !== undefined ? { ws_fallback_enabled: opts.ws_fallback_enabled } : {}),
  }).then(r => r.data);

// iter150AG Part 12 (P2) — webhook + WS consumer status (super-admin).
export const seqCpWebhookStatus = () =>
  api.get(`/sequencing/admin/cp-webhook/status`).then(r => r.data);

// iter150AG Part 14 — passive verify diagnostic.
export const seqCpWebhookVerify = (window_seconds = 300) =>
  api.get(`/sequencing/admin/cp-webhook/verify`, { params: { window_seconds } }).then(r => r.data);
export const seqCpConfigTest = (base_url, api_key) =>
  api.post(`/sequencing/admin/control-plane/test`, { base_url, api_key }).then(r => r.data);
export const seqCpConfigReset = () =>
  api.post(`/sequencing/admin/control-plane/reset`).then(r => r.data);

// ── Phase 2 — On-demand picker + group-from-compressors ─────────────
export const seqAvailableCompressors = (params = {}) =>
  api.get(`/sequencing/compressors/available`, { params }).then(r => r.data);
export const seqCreateGroupFromCompressors = (payload) =>
  api.post(`/sequencing/groups/from-compressors`, payload).then(r => r.data);

// ── iter150AG.onboarding — Admin-managed device-template mapping ────
export const seqListDeviceTemplates = () =>
  api.get(`/sequencing/admin/device-templates`).then(r => r.data);
export const seqListCpDeviceTemplates = () =>
  api.get(`/sequencing/admin/cp-device-templates`).then(r => r.data);
export const seqCreateDeviceTemplate = (payload) =>
  api.post(`/sequencing/admin/device-templates`, payload).then(r => r.data);
export const seqUpdateDeviceTemplate = (tid, payload) =>
  api.patch(`/sequencing/admin/device-templates/${tid}`, payload).then(r => r.data);
export const seqDeleteDeviceTemplate = (tid) =>
  api.delete(`/sequencing/admin/device-templates/${tid}`).then(r => r.data);
export const seqResolveDeviceTemplate = (params = {}) =>
  api.post(`/sequencing/admin/device-templates/resolve`, null, { params }).then(r => r.data);

// ── iter150AG.onboarding — per-compressor onboarding ────────────────
export const seqOnboardingPreview = (serial) =>
  api.get(`/sequencing/onboarding/compressors/${encodeURIComponent(serial)}/preview`).then(r => r.data);
export const seqOnboardingProvision = (serial, payload = {}) =>
  api.post(`/sequencing/onboarding/compressors/${encodeURIComponent(serial)}/provision`, payload).then(r => r.data);
export const seqOnboardingState = (serial) =>
  api.get(`/sequencing/onboarding/compressors/${encodeURIComponent(serial)}`).then(r => r.data);
export const seqListOnboarded = (params = {}) =>
  api.get(`/sequencing/onboarding/compressors`, { params }).then(r => r.data);

// ── type catalog ───────────────────────────────────────────────────
export const seqListTypes = () =>
  api.get("/sequencing/types").then(r => r.data);
export const seqCreateType = (payload) =>
  api.post("/sequencing/types", payload).then(r => r.data);
export const seqUpdateType = (type_code, payload) =>
  api.put(`/sequencing/types/${type_code}`, payload).then(r => r.data);
export const seqDeleteType = (type_code) =>
  api.delete(`/sequencing/types/${type_code}`).then(r => r.data);

// ── disclaimers ────────────────────────────────────────────────────
export const seqListDisclaimers = (kind) =>
  api.get("/sequencing/disclaimers", { params: kind ? { kind } : {} }).then(r => r.data);
export const seqActiveDisclaimer = (kind) =>
  api.get(`/sequencing/disclaimers/active/${kind}`).then(r => r.data);
export const seqCreateDisclaimer = (payload) =>
  api.post("/sequencing/disclaimers", payload).then(r => r.data);
export const seqAcceptDisclaimer = (payload) =>
  api.post("/sequencing/disclaimers/accept", payload).then(r => r.data);

// ── holidays ───────────────────────────────────────────────────────
export const seqListHolidays = (params) =>
  api.get("/sequencing/holidays", { params }).then(r => r.data);
export const seqCreateHoliday = (payload) =>
  api.post("/sequencing/holidays", payload).then(r => r.data);
export const seqPatchHoliday = (hid, payload) =>
  api.patch(`/sequencing/holidays/${hid}`, payload).then(r => r.data);
export const seqDeleteHoliday = (hid) =>
  api.delete(`/sequencing/holidays/${hid}`).then(r => r.data);

// ── Control Plane passthroughs ─────────────────────────────────────
export const seqListDevices = (tenant_id) =>
  api.get("/sequencing/control-plane/devices", { params: tenant_id ? { tenant_id } : {} }).then(r => r.data);
export const seqDeviceParameters = (deviceId) =>
  api.get(`/sequencing/control-plane/devices/${deviceId}/parameters`).then(r => r.data);
